import { act_com } from '@lc/action/cardType/Common';
import {
	handSize1,
	handSizeMinus3,
	neighborFactoryEnergyColor,
	noHandNextSeason,
	noSummitCards,
	noWinterHand,
	recharge1,
} from '@lc/const/modifiers';
import { GameState } from '@lc/interface/gameState/types';
import { act_player } from '@lc/interface/player/action';
import { getRandomArrayIndex } from '@lc/lib/array';
import { getPlayerStateWithId } from '@lc/lib/player_helper';

// effects that are too specific to be considered "common", stuff only on a contract card

class ContractAction {
	acCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;

		let playerIdsLowestContribution: number[] = [];
		const lowestContribution = Infinity;

		for (const player of gameState.playerStateArray) {
			const pContribution = player.donationCupArr.length;

			if (pContribution < lowestContribution) {
				playerIdsLowestContribution = [player.playerId];
			} else if (pContribution === lowestContribution) {
				playerIdsLowestContribution.push(player.playerId);
			}
		}

		return !playerIdsLowestContribution.includes(playerId);
	}

	acPenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;

		const neighborIdArr = act_com.createNeighborIdArray(
			playerData,
			gameState.playerStateArray
		);

		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);
		const leftState = gameState.playerStateArray[neighborIdArr[0]];
		const rightState = gameState.playerStateArray[neighborIdArr[2]];

		if (pState.batteries === 0) {
			return;
		}
		act_com.giveBatteryToTarget(pState, leftState);

		if (pState.batteries === 0) {
			return;
		}
		act_com.giveBatteryToTarget(pState, rightState);
	}

	centerfoldCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.completedAwards.length >= 2;
	}

	centerfoldPenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		const numFailedContracts = pState.failedContracts.length + 1;

		for (let i = 0; i < numFailedContracts * 3; i++) {
			pState.donationCupArr.pop();
		}
	}

	charitableDonationCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		let playerIdsHighestContribution: number[] = [];
		const highestContribution = 0;

		for (const player of gameState.playerStateArray) {
			const pContribution = player.donationCupArr.length;

			if (pContribution > highestContribution) {
				playerIdsHighestContribution = [player.playerId];
			} else if (pContribution === highestContribution) {
				playerIdsHighestContribution.push(player.playerId);
			}
		}

		return (
			playerIdsHighestContribution.length === 1 &&
			playerIdsHighestContribution[0] === playerId
		);
	}

	charitableDonationPenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const players = gameState.playerStateArray;

		for (const p of players) {
			if (p.playerId === playerId) continue;

			if (p.carbonFootprint > 0) {
				p.carbonFootprint--;
				players[playerId].carbonFootprint++;
			}
		}
	}

	comedyOfCommonsCondition(playerData: any, gameState: GameState) {
		// const { playerId } = playerData;
		const players = gameState.playerStateArray;

		const neighborIdArr = act_com.createNeighborIdArray(
			playerData,
			players
		);

		let totalPR = 0;
		let totalCarbonFootprint = 0;
		for (let i = 0; i < neighborIdArr.length; i++) {
			const currId = neighborIdArr[i];
			totalPR += players[currId].prArr.length;
			totalCarbonFootprint += players[currId].carbonFootprint;
		}

		return totalPR > totalCarbonFootprint;
	}

	comedyOfCommonsPenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const players = gameState.playerStateArray;

		act_player.removeDonation(players[playerId], 4); // TODO recheck how donations work rn, is it a number, array or series of arrays

		// for each player, if current number is a neighbor of our current player, remove
		const neighborIds = act_com.getNeighborPlayerIds(
			playerId,
			players
		) as any;

		for (let i = 0; i < neighborIds.length; i++) {
			const nId = neighborIds[i];

			act_player.removeDonation(players[nId], 2);
		}

		return true;
	}

	chargesItself(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.batteries >= 2 && pState.batteryEnergyArr.length >= 2;
	}

	chernobyl(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.factoriesBuiltThisSeason >= 2;
	}

	dinosaurCondition(playerData: any, gameState: GameState) {
		// const { playerId } = playerData;
		const players = gameState.playerStateArray;

		let carbonTally = 0;
		let prTally = 0;
		for (const p of players) {
			carbonTally += p.carbonFootprint;
			prTally += p.prArr.length;
		}

		return prTally > carbonTally;
	}

	dinosaurPenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const neighborIdArr = act_com.createNeighborIdArray(
			playerData,
			gameState.playerStateArray
		);

		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);
		const leftState = gameState.playerStateArray[neighborIdArr[0]];
		const rightState = gameState.playerStateArray[neighborIdArr[2]];

		const halfPR = Math.ceil(pState.prArr.length / 2);

		leftState.prArr.push(...pState.prArr.splice(0, halfPR));
		rightState.prArr.push(...pState.prArr);
		pState.prArr = [];
	}

	dubai(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.factoriesBuiltThisSeasonFromCarbonOnly >= 1;
	}

	flatEarthFunding(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		act_com.gainFactory(playerData, gameState);
		act_com.rechargeAllBatteries(playerData, gameState);

		pState.summitCards = [];
		pState.modifiers.push(noSummitCards);
	}

	freezingPointCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.completedContracts.length >= 3;
	}

	freezingPointPenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);
		const tState = getPlayerStateWithId(
			gameState.playerStateArray,
			pState.freezingPointStolenPlayerId
		);

		const energyStolenAmount = Math.min(pState.donationCupArr.length, 5);

		for (let i = 0; i < energyStolenAmount; i++) {
			const stolenEnergy = pState.donationCupArr.pop();
			if (stolenEnergy == null) {
				return;
			}
			tState.donationCupArr.push(stolenEnergy);
		}
	}

	generateAndHibernate(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return (
			pState.factoriesBuiltThisSeason >= 1 &&
			pState.batteriesBuiltThisSeason >= 2
		);
	}

	greenLogoCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.carbonFootprint <= 1;
	}

	hashtagCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.prArr.length >= 5;
	}

	hashtagPenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		const neighborIdArr = act_com.createNeighborIdArray(
			playerData,
			gameState.playerStateArray
		);

		pState.modifiers.push(neighborFactoryEnergyColor);
		pState.neighborFactoryEnergyColorId = neighborIdArr[0]; // default player to left for now
	}

	liberalTearsCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.factoriesBuiltThisSeason === 0;
	}

	liberalTearsFunding(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);
		act_player.refillBattery(pState);

		return pState.modifiers.push(recharge1);
	}

	lowestBidderCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.equipment.length >= 0;
	}

	lowestBidderPentaly(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		pState.modifiers.push(noWinterHand);
	}

	origamiCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.seasonalCards.length >= 4;
	}

	origamiPenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		if (pState.summitCards.length === 0) {
			return;
		}

		const randomIndex = getRandomArrayIndex(pState.summitCards);
		pState.summitCards.splice(randomIndex, 1); // Remove 1 element at randomIndex
	}

	pyramidCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.donationCupArr.length === 0;
	}

	rainyDayCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.batteries >= 2 && pState.batteryEnergyArr.length >= 2;
	}

	rainyDayPenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		act_player.deleteBattery(pState);
		if (pState.prArr.length > 1) {
			pState.prArr.pop();
		}
		if (pState.prArr.length > 1) {
			pState.prArr.pop();
		}
	}

	rcCarCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.batteries >= 3 && pState.batteryEnergyArr.length >= 3;
	}

	rcCarPenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		pState.modifiers.push(handSizeMinus3);
	}

	scholarshipCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.prArr.length >= 7;
	}

	scholarshipPenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;

		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);
		if (pState.factories === 0) {
			return;
		}

		const neighborIdArr = act_com.createNeighborIdArray(
			playerData,
			gameState.playerStateArray
		);

		const currLeastFactorires = Infinity;
		let neighborIdWithLeastFactories = -1;

		for (let i = 0; i < neighborIdArr.length; i) {
			const currId = neighborIdArr[i];
			if (currId === playerId) {
				continue;
			}

			const currPlayerState = gameState.playerStateArray[currId];

			if (currPlayerState.factories < currLeastFactorires) {
				neighborIdWithLeastFactories = currId;
			}
		}

		const tState = getPlayerStateWithId(
			gameState.playerStateArray,
			neighborIdWithLeastFactories
		);

		tState.factories += 1;

		if (pState.factories === pState.factoryEnergyArr.length) {
			tState.factoryEnergyArr.push(pState.factoryEnergyArr[0]);
		}

		act_player.deleteFactory(pState);
	}

	sickOfWinningCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.failedContracts.length === 0;
	}

	sickOfWinningFunding(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		const neighborIdArr = act_com.createNeighborIdArray(
			playerData,
			gameState.playerStateArray
		);
		const leftState = gameState.playerStateArray[neighborIdArr[0]];
		const rightState = gameState.playerStateArray[neighborIdArr[2]];

		const rechargeAmount =
			act_com.getPlayerNumActiveContracts(leftState) +
			act_com.getPlayerNumActiveContracts(rightState);

		for (let i = 0; i < rechargeAmount; i++) {
			act_player.refillBattery(pState);
		}
	}

	sickOfWinningPenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);
		const neighborIdArr = act_com.createNeighborIdArray(
			playerData,
			gameState.playerStateArray
		);
		const leftState = gameState.playerStateArray[neighborIdArr[0]];

		act_com.giveFactoryToTarget(pState, leftState);
	}

	stockingCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.prArr.length >= 4;
	}

	stockingPenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		pState.modifiers.push(noHandNextSeason);
	}

	termsAndConditionsCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.completedContracts.length >= 3;
	}

	termsAndConditionsPenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		const neighborIdArr = act_com.createNeighborIdArray(
			playerData,
			gameState.playerStateArray
		);
		const leftState = gameState.playerStateArray[neighborIdArr[0]];
		const leftNumBatteries = leftState.batteries;

		for (let i = 0; i < leftNumBatteries; i++) {
			pState.donationCupArr.pop();
		}
	}

	townCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.prArr.length >= 6;
	}

	townPenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		act_player.deleteFactory(pState);
	}

	triangleCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		return pState.prArr.length >= 3;
	}

	trianglePenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;

		const neighborIdArr = act_com.createNeighborIdArray(
			playerData,
			gameState.playerStateArray
		);

		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);
		const tState = gameState.playerStateArray[neighborIdArr[2]];

		if (pState.batteries === 0) {
			return;
		}

		act_com.giveBatteryToTarget(pState, tState);
	}

	workhorseCondition(playerData: any, gameState: GameState) {
		const { playerId } = playerData;

		let playerIdsHighestContribution: number[] = [];
		const highestContribution = 0;

		for (const player of gameState.playerStateArray) {
			const pContribution = player.donationCupArr.length;

			if (pContribution > highestContribution) {
				playerIdsHighestContribution = [player.playerId];
			} else if (pContribution === highestContribution) {
				playerIdsHighestContribution.push(player.playerId);
			}
		}

		return (
			playerIdsHighestContribution.length === 1 &&
			playerIdsHighestContribution[0] === playerId
		);
	}

	workhorsePenalty(playerData: any, gameState: GameState) {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		pState.modifiers.push(handSize1);
	}
}

export const act_contract = new ContractAction();
